import { Collapsible, FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import { LanguageEvaluationResult } from 'lib-shared-types-api';
import { ReactNode, useState } from 'react';

import { LanguageEvaluationList } from './languageEvaluationList';

type FilterItemWithDetailsProps = {
  /** The main text content to display */
  children: ReactNode;
  /** Contact point value (e.g., email address or phone number) */
  contactPointValue: string | null;
  /** Language evaluations to display in expandable section */
  languageEvaluations: readonly LanguageEvaluationResult[];
  /** Whether to show subdued styling (for failed/non-selected items) */
  subdued?: boolean;
};

/**
 * Reusable wrapper for filter evaluation items with expandable details. Handles
 * expansion state and displays contact point + language evaluations.
 */
export const FilterItemWithDetails = ({
  children,
  contactPointValue,
  languageEvaluations,
  subdued = false,
}: FilterItemWithDetailsProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasExpandableDetails = languageEvaluations.length > 0;

  return (
    <FlexBox flexDirection="column" gap="100">
      <FlexBox gap="100" alignItems="center">
        <Text.Paragraph variant="3" subdued={subdued}>
          {children}
        </Text.Paragraph>
        {hasExpandableDetails && (
          <Icon
            name={isExpanded ? 'angle-up' : 'angle-down'}
            size={16}
            style={{ cursor: 'pointer' }}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        )}
      </FlexBox>

      {hasExpandableDetails && (
        <Collapsible isOpen={isExpanded} header={null}>
          <FlexBox
            flexDirection="column"
            gap="100"
            padding="0 0 0 300"
            margin="0 0 0 400"
          >
            {contactPointValue && (
              <Text.Paragraph variant="4" subdued>
                Contact: {contactPointValue}
              </Text.Paragraph>
            )}
            <FlexBox flexDirection="column" gap="50">
              <Text.Paragraph variant="4" subdued>
                Languages:
              </Text.Paragraph>
              <LanguageEvaluationList
                languageEvaluations={languageEvaluations}
              />
            </FlexBox>
          </FlexBox>
        </Collapsible>
      )}
    </FlexBox>
  );
};
